import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useUser } from '@/hooks/auth/use-user';
import { userQueryKeys } from '@/lib/query-keys';

export interface UsageOverview {
  total_requests: number;
  total_credits: number;
  most_used_feature: string;
  most_used_feature_percentage: number;
  most_used_app: string;
  current_month_usage_percentage: number;
}

export interface UsageLog {
  id: string;
  created_at: string;
  app_name: string;
  function_name: string;
  service_provider: string;
  operation_type: string;
  credits: number;
  total_count: number;
}

export interface UsageAnalytics {
  app_name: string;
  app_requests: number;
  app_credits: number;
  feature_name: string;
  feature_percentage: number;
  feature_color: string;
}

export interface CostSummary {
  total_credits: number;
  average_credits_per_request: number;
  trend_percentage: number;
  daily_costs: any;
}

// Hook for usage overview stats
export function useUsageOverview(timeRange: string = '30d') {
  const { user } = useUser();
  
  return useQuery({
    queryKey: userQueryKeys.usageOverview(user?.id, timeRange),
    queryFn: async (): Promise<UsageOverview> => {
      if (!user?.id) throw new Error('User not authenticated');
      
      const { data, error } = await supabase
        .rpc('get_user_usage_overview', {
          user_uuid: user.id,
          time_range: timeRange
        });
      
      if (error) throw error;
      return data?.[0] || {
        total_requests: 0,
        total_credits: 0,
        most_used_feature: 'No Usage',
        most_used_feature_percentage: 0,
        most_used_app: 'No Usage',
        current_month_usage_percentage: 0
      };
    },
    enabled: !!user?.id,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

// Hook for usage logs (paginated)
export function useUsageLogs(
  timeRange: string = '30d',
  limit: number = 50,
  offset: number = 0,
  appFilter?: string,
  serviceFilter?: string
) {
  const { user } = useUser();
  
  return useQuery({
    queryKey: userQueryKeys.usageLogs(user?.id, timeRange, limit, offset, appFilter, serviceFilter),
    queryFn: async (): Promise<UsageLog[]> => {
      if (!user?.id) throw new Error('User not authenticated');
      
      const { data, error } = await supabase
        .rpc('get_user_usage_logs', {
          user_uuid: user.id,
          time_range: timeRange,
          page_limit: limit,
          page_offset: offset,
          app_filter: appFilter || null,
          service_filter: serviceFilter || null
        });
      
      if (error) throw error;
      return data || [];
    },
    enabled: !!user?.id,
    refetchOnWindowFocus: false,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
}

// Hook for usage analytics
export function useUsageAnalytics(timeRange: string = '30d') {
  const { user } = useUser();
  
  return useQuery({
    queryKey: userQueryKeys.usageAnalytics(user?.id, timeRange),
    queryFn: async (): Promise<UsageAnalytics[]> => {
      if (!user?.id) throw new Error('User not authenticated');
      
      const { data, error } = await supabase
        .rpc('get_user_usage_analytics', {
          user_uuid: user.id,
          time_range: timeRange
        });
      
      if (error) throw error;
      return data || [];
    },
    enabled: !!user?.id,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

// Hook for cost summary
export function useCostSummary(timeRange: string = '30d') {
  const { user } = useUser();
  
  return useQuery({
    queryKey: userQueryKeys.costSummary(user?.id, timeRange),
    queryFn: async (): Promise<CostSummary> => {
      if (!user?.id) throw new Error('User not authenticated');
      
      const { data, error } = await supabase
        .rpc('get_user_cost_summary', {
          user_uuid: user.id,
          time_range: timeRange
        });
      
      if (error) throw error;
      return data?.[0] || {
        total_credits: 0,
        average_credits_per_request: 0,
        trend_percentage: 0,
        daily_costs: []
      };
    },
    enabled: !!user?.id,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
} 